import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import SearchableSelect from "../../../components/DataEntry/SearchableSelect";
import {
  CountableItem,
  InventoryCount,
  InventoryCountDetail,
  InventoryCountLine,
  InventoryCountLookups,
  Warehouse,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

/** Ispod ove razlike se ne knjiži ništa — ista tolerancija kao na serveru. */
const EPSILON_KG = 0.001;

type LineFilter = "all" | "uncounted" | "differences" | "no-stock";

interface LineDraft {
  counted: string;
  notes: string;
  reasonId: number | null;
}

const STATUS_LABEL: Record<string, string> = {
  planned: "Planirana",
  counting: "U popisu",
  reconciling: "Usklađivanje",
  closed: "Proknjižena",
  cancelled: "Otkazana",
};

const STATUS_CLASS: Record<string, string> = {
  planned: "badge-draft",
  counting: "badge-quarantine",
  reconciling: "badge-info",
  closed: "badge-success",
  cancelled: "badge-danger",
};

const TYPE_LABEL: Record<string, string> = {
  monthly: "Mjesečna",
  cycle: "Ciklička",
  spot: "Kontrolna",
  annual: "Godišnja",
  initial: "Inicijalni unos",
};

const today = () => new Date().toISOString().split("T")[0];

/** Popis se potpisuje prijavljenim korisnikom; kolona je ID, ne ime. */
const currentUserId = (): number | undefined => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return undefined;
    const user = JSON.parse(raw);
    const id = user?.ID ?? user?.id ?? user?.UserID ?? user?.user?.ID;
    return Number.isFinite(Number(id)) ? Number(id) : undefined;
  } catch {
    return undefined;
  }
};

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("hr-HR") : "—";

const formatKg = (n: number | null | undefined) =>
  n == null ? "—" : n.toFixed(3).replace(/\.?0+$/, "") || "0";

/** Prazan string znači "nije popisano", što nije isto kao popisana nula. */
const parseCounted = (raw: string): number | null => {
  const trimmed = raw.trim().replace(",", ".");
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) ? n : null;
};

const Inventura = () => {
  savePathToLocalStorage();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [lookups, setLookups] = useState<InventoryCountLookups | null>(null);
  const [counts, setCounts] = useState<InventoryCount[]>([]);
  const [active, setActive] = useState<InventoryCountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [filterWarehouseId, setFilterWarehouseId] = useState<number | "">("");
  const [filterStatus, setFilterStatus] = useState("");

  const [drafts, setDrafts] = useState<Record<number, LineDraft>>({});
  const [lineFilter, setLineFilter] = useState<LineFilter>("all");
  const [search, setSearch] = useState("");

  const [showNewModal, setShowNewModal] = useState(false);
  const [showAddLineModal, setShowAddLineModal] = useState(false);

  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // ==================== UČITAVANJE ====================

  useEffect(() => {
    mesApi.getSkladista().then(setWarehouses).catch(() => {});
    mesApi.getInventoryCountLookups().then(setLookups).catch(() => {});
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mesApi.getInventoryCounts({
        warehouse_id: filterWarehouseId || undefined,
        status: filterStatus || undefined,
      });
      setCounts(data || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterWarehouseId, filterStatus]);

  useEffect(() => {
    if (!active) loadCounts();
  }, [active, loadCounts]);

  const openCount = async (id: number) => {
    try {
      setLoading(true);
      const data = await mesApi.getInventoryCount(id);
      setActive(data);
      setDrafts({});
      setLineFilter("all");
      setSearch("");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeCount = () => {
    setActive(null);
    setDrafts({});
  };

  // ==================== UREĐIVANJE REDAKA ====================

  const getDraft = useCallback(
    (line: InventoryCountLine): LineDraft =>
      drafts[line.line_id] ?? {
        counted: line.counted_quantity_kg == null ? "" : String(line.counted_quantity_kg),
        notes: line.notes ?? "",
        reasonId: line.reason_id,
      },
    [drafts]
  );

  const setDraft = (line: InventoryCountLine, patch: Partial<LineDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [line.line_id]: { ...getDraft(line), ...patch },
    }));
  };

  /** Redci koji se stvarno razlikuju od spremljenog stanja. */
  const dirtyLines = useMemo(() => {
    if (!active) return [];
    return active.lines
      .filter((line) => {
        const draft = drafts[line.line_id];
        if (!draft) return false;
        return (
          parseCounted(draft.counted) !== line.counted_quantity_kg ||
          (draft.notes.trim() || null) !== (line.notes || null) ||
          draft.reasonId !== line.reason_id
        );
      })
      .map((line) => {
        const draft = drafts[line.line_id];
        return {
          line_id: line.line_id,
          counted_quantity_kg: parseCounted(draft.counted),
          notes: draft.notes.trim(),
          reason_id: draft.reasonId,
        };
      });
  }, [active, drafts]);

  const saveDrafts = async (): Promise<InventoryCountDetail | null> => {
    if (!active || dirtyLines.length === 0) return active;
    const data = await mesApi.saveInventoryCountLines(active.inventory_count_id, dirtyLines);
    setActive(data);
    setDrafts({});
    return data;
  };

  const handleSave = async () => {
    if (dirtyLines.length === 0) {
      toast.info("Nema promjena za spremanje");
      return;
    }
    try {
      setSaving(true);
      await saveDrafts();
      toast.success("Popis spremljen");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  /** Stavke bez razlike se ne moraju tipkati — preuzmi knjigovodstveno stanje. */
  const handleFillFromBook = () => {
    if (!active) return;
    const next = { ...drafts };
    let filled = 0;
    for (const line of visibleLines) {
      if (getDraft(line).counted.trim()) continue;
      if (line.book_quantity_kg === 0 && line.is_new_lot) continue;
      next[line.line_id] = { ...getDraft(line), counted: String(line.book_quantity_kg) };
      filled++;
    }
    if (filled === 0) {
      toast.info("Nema praznih stavki sa stanjem");
      return;
    }
    setDrafts(next);
    toast.success(`Popunjeno ${filled} stavki — provjerite i ispravite odstupanja`);
  };

  const handleRemoveLine = async (lineId: number) => {
    if (!active) return;
    try {
      const data = await mesApi.removeInventoryCountLine(active.inventory_count_id, lineId);
      setActive(data);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handlePost = async () => {
    if (!active) return;
    try {
      setSaving(true);
      const current = dirtyLines.length > 0 ? await saveDrafts() : active;
      if (!current) return;

      if (current.counted_lines === 0) {
        toast.error("Popišite barem jednu stavku prije knjiženja");
        return;
      }
      const confirmed = window.confirm(
        `Proknjižiti inventuru ${current.count_number}?\n\n` +
          `Popisano: ${current.counted_lines} / ${current.total_lines} stavki\n` +
          `Viškovi: +${formatKg(current.surplus_kg)} kg\n` +
          `Manjkovi: -${formatKg(current.shortage_kg)} kg\n\n` +
          `Stanje skladišta bit će usklađeno s popisanim količinama. ` +
          `Nepopisane stavke ostaju nepromijenjene.`
      );
      if (!confirmed) return;

      const data = await mesApi.postInventoryCount(current.inventory_count_id, currentUserId());
      setActive(data);
      toast.success("Inventura proknjižena — stanje usklađeno");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCount = async () => {
    if (!active) return;
    if (!window.confirm(`Otkazati inventuru ${active.count_number}? Popis se neće proknjižiti.`)) return;
    try {
      setSaving(true);
      const data = await mesApi.cancelInventoryCount(active.inventory_count_id);
      setActive(data);
      toast.success("Inventura otkazana");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ==================== FILTRIRANJE ====================

  const visibleLines = useMemo(() => {
    if (!active) return [];
    const q = search.trim().toLowerCase();
    return active.lines.filter((line) => {
      if (q) {
        const haystack = `${line.material_name ?? ""} ${line.lot_number ?? ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      const counted = parseCounted(getDraft(line).counted);
      switch (lineFilter) {
        case "uncounted":
          return counted == null;
        case "differences":
          return counted != null && Math.abs(counted - line.book_quantity_kg) > EPSILON_KG;
        case "no-stock":
          return line.book_quantity_kg === 0;
        default:
          return true;
      }
    });
  }, [active, search, lineFilter, getDraft]);

  /** Sažetak se računa iz nacrta da brojke prate tipkanje, ne zadnji save. */
  const liveSummary = useMemo(() => {
    if (!active) return { counted: 0, surplus: 0, shortage: 0, value: 0 };
    let counted = 0;
    let surplus = 0;
    let shortage = 0;
    let value = 0;
    for (const line of active.lines) {
      const qty = parseCounted(getDraft(line).counted);
      if (qty == null) continue;
      counted++;
      const delta = qty - line.book_quantity_kg;
      if (delta > EPSILON_KG) surplus += delta;
      else if (delta < -EPSILON_KG) shortage += -delta;
      value += delta * (line.unit_price ?? 0);
    }
    return { counted, surplus, shortage, value };
  }, [active, getDraft]);

  /** Enter u polju za količinu vodi na sljedeći redak — popis se unosi u nizu. */
  const focusNextInput = (index: number) => {
    for (let i = index + 1; i < visibleLines.length; i++) {
      const el = inputRefs.current[visibleLines[i].line_id];
      if (el) {
        el.focus();
        el.select();
        return;
      }
    }
  };

  const editable = Boolean(active?.is_editable);

  // ==================== RENDER ====================

  const renderList = () => (
    <>
      <div className="iwt-header">
        <div>
          <h2>Inventura</h2>
          <p className="iwt-role-hint">
            Popis stvarnog stanja skladišta i knjiženje razlika. Koristi se i za inicijalni
            unos zaliha kad se skladište tek pušta u rad.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowNewModal(true)}>
          + Nova inventura
        </button>
      </div>

      <div className="wh-pregled-toolbar">
        <select
          className="filter-select"
          value={filterWarehouseId}
          onChange={(e) => setFilterWarehouseId(e.target.value ? Number(e.target.value) : "")}
        >
          <option value="">Sva skladišta</option>
          {warehouses.map((w) => (
            <option key={w.warehouse_id} value={w.warehouse_id}>
              {w.name}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Svi statusi</option>
          {(lookups?.statuses ?? []).map((s) => (
            <option key={s.id} value={s.code ?? ""}>
              {STATUS_LABEL[s.code ?? ""] ?? s.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary" onClick={loadCounts} disabled={loading}>
          Osvježi
        </button>
      </div>

      {loading ? (
        <p className="iwt-empty">Učitavanje...</p>
      ) : counts.length === 0 ? (
        <p className="iwt-empty">
          Nema inventura. Otvorite novu da popišete stanje ili unesete početne zalihe.
        </p>
      ) : (
        <div className="mes-section">
          <div className="mes-table-wrap">
            <table className="mes-table">
            <thead>
              <tr>
                <th>Broj</th>
                <th>Datum</th>
                <th>Skladište</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Popisano</th>
                <th style={{ textAlign: "right" }}>Višak (kg)</th>
                <th style={{ textAlign: "right" }}>Manjak (kg)</th>
                <th style={{ textAlign: "right" }}>Razlika (€)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {counts.map((c) => (
                <tr key={c.inventory_count_id}>
                  <td>
                    {c.count_number}
                    <span className="iwt-line-note">
                      {TYPE_LABEL[c.type ?? ""] ?? c.type_name ?? "—"}
                    </span>
                  </td>
                  <td>{formatDate(c.count_date)}</td>
                  <td>{c.warehouse_name ?? "—"}</td>
                  <td>
                    <span className={STATUS_CLASS[c.status ?? ""] ?? "badge-info"}>
                      {STATUS_LABEL[c.status ?? ""] ?? c.status_name ?? "—"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                    {c.counted_lines} / {c.total_lines}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                    {c.surplus_kg ? `+${formatKg(c.surplus_kg)}` : "—"}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                    {c.shortage_kg ? `-${formatKg(c.shortage_kg)}` : "—"}
                  </td>
                  <td
                    style={{ textAlign: "right", fontFamily: "monospace" }}
                    className={c.difference_value < 0 ? "inv-negative" : undefined}
                  >
                    {c.difference_value ? c.difference_value.toFixed(2) : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => openCount(c.inventory_count_id)}
                    >
                      {c.is_editable ? "Popiši" : "Pregled"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </>
  );

  const renderDetail = (count: InventoryCountDetail) => (
    <>
      <div className="iwt-header">
        <div>
          <h2>
            {count.count_number}{" "}
            <span className={STATUS_CLASS[count.status ?? ""] ?? "badge-info"}>
              {STATUS_LABEL[count.status ?? ""] ?? count.status_name ?? "—"}
            </span>
          </h2>
          <p className="iwt-role-hint">
            {count.warehouse_name} · {formatDate(count.count_date)} ·{" "}
            {TYPE_LABEL[count.type ?? ""] ?? count.type_name ?? "—"}
            {count.posted_at ? ` · proknjižena ${formatDate(count.posted_at)}` : ""}
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={closeCount}>
          ← Sve inventure
        </button>
      </div>

      <div className="inv-summary">
        <div className="inv-summary-item">
          <span className="inv-summary-label">Stavke</span>
          <strong>
            {liveSummary.counted} / {count.total_lines}
          </strong>
        </div>
        <div className="inv-summary-item">
          <span className="inv-summary-label">Viškovi</span>
          <strong className="inv-positive">+{formatKg(liveSummary.surplus)} kg</strong>
        </div>
        <div className="inv-summary-item">
          <span className="inv-summary-label">Manjkovi</span>
          <strong className="inv-negative">-{formatKg(liveSummary.shortage)} kg</strong>
        </div>
        <div className="inv-summary-item">
          <span className="inv-summary-label">Vrijednost razlike</span>
          <strong className={liveSummary.value < 0 ? "inv-negative" : "inv-positive"}>
            {liveSummary.value.toFixed(2)} €
          </strong>
        </div>
      </div>

      <div className="wh-pregled-toolbar">
        <input
          type="text"
          className="filter-select"
          style={{ minWidth: 220 }}
          placeholder="Pretraži stavku ili LOT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="filter-select"
          value={lineFilter}
          onChange={(e) => setLineFilter(e.target.value as LineFilter)}
        >
          <option value="all">Sve stavke</option>
          <option value="uncounted">Nepopisane</option>
          <option value="differences">Samo razlike</option>
          <option value="no-stock">Bez knjigovodstvenog stanja</option>
        </select>
        {editable && (
          <>
            <button type="button" className="btn-secondary" onClick={handleFillFromBook}>
              Popuni stanjem
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowAddLineModal(true)}
            >
              + Dodaj stavku
            </button>
          </>
        )}
        <span className="iwt-progress">
          Prikazano {visibleLines.length} od {count.lines.length}
        </span>
      </div>

      <div className="mes-section">
        <div className="iwt-table-scroll">
          <table className="mes-table inv-table">
            <thead>
              <tr>
                <th>Stavka</th>
                <th>LOT</th>
                <th>Rok</th>
                <th style={{ textAlign: "right" }}>Stanje (kg)</th>
                <th style={{ textAlign: "right" }}>Popisano (kg)</th>
                <th style={{ textAlign: "right" }}>Razlika</th>
                <th>Razlog</th>
                <th>Napomena</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {visibleLines.length === 0 ? (
                <tr>
                  <td colSpan={editable ? 9 : 8} className="iwt-empty">
                    Nema stavki za prikaz
                  </td>
                </tr>
              ) : (
                visibleLines.map((line, index) => {
                  const draft = getDraft(line);
                  const counted = parseCounted(draft.counted);
                  const delta = counted == null ? null : counted - line.book_quantity_kg;
                  const hasDelta = delta != null && Math.abs(delta) > EPSILON_KG;
                  return (
                    <tr
                      key={line.line_id}
                      className={
                        hasDelta ? "iwt-line-warn" : counted != null ? "iwt-line-done" : ""
                      }
                    >
                      <td>
                        {line.material_name}
                        {line.material_type && (
                          <span className="iwt-line-note">{line.material_type}</span>
                        )}
                      </td>
                      <td>
                        {line.lot_number || (line.is_new_lot ? <em>novi LOT</em> : "—")}
                      </td>
                      <td>{line.expiry_date ? formatDate(line.expiry_date) : "—"}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                        {formatKg(line.book_quantity_kg)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {editable ? (
                          <input
                            ref={(el) => {
                              inputRefs.current[line.line_id] = el;
                            }}
                            type="text"
                            inputMode="decimal"
                            className="input-classic input-sm inv-qty-input"
                            value={draft.counted}
                            placeholder="—"
                            onChange={(e) => setDraft(line, { counted: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                focusNextInput(index);
                              }
                            }}
                          />
                        ) : (
                          <span style={{ fontFamily: "monospace" }}>
                            {formatKg(line.counted_quantity_kg)}
                          </span>
                        )}
                      </td>
                      <td
                        style={{ textAlign: "right", fontFamily: "monospace" }}
                        className={
                          !hasDelta ? undefined : delta! > 0 ? "inv-positive" : "inv-negative"
                        }
                      >
                        {delta == null ? "—" : `${delta > 0 ? "+" : ""}${formatKg(delta)}`}
                      </td>
                      <td>
                        {/* Razlog ima smisla tražiti tek kad razlika postoji. */}
                        {editable && hasDelta ? (
                          <select
                            className="input-classic input-sm"
                            value={draft.reasonId ?? ""}
                            onChange={(e) =>
                              setDraft(line, {
                                reasonId: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {(lookups?.reasons ?? []).map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          lookups?.reasons.find((r) => r.id === line.reason_id)?.name ?? "—"
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <input
                            type="text"
                            className="input-classic input-sm"
                            value={draft.notes}
                            onChange={(e) => setDraft(line, { notes: e.target.value })}
                          />
                        ) : (
                          line.notes || "—"
                        )}
                      </td>
                      {editable && (
                        <td style={{ textAlign: "right" }}>
                          {line.is_new_lot && !line.material_lot_id && (
                            <button
                              type="button"
                              className="btn-cancel btn-sm"
                              title="Ukloni stavku iz popisa"
                              onClick={() => handleRemoveLine(line.line_id)}
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editable && (
        <div className="inv-actions">
          <button type="button" className="btn-cancel" onClick={handleCancelCount} disabled={saving}>
            Otkaži inventuru
          </button>
          <div className="inv-actions-right">
            <button type="button" className="btn-secondary" onClick={handleSave} disabled={saving}>
              {saving ? "Spremanje..." : `Spremi popis${dirtyLines.length ? ` (${dirtyLines.length})` : ""}`}
            </button>
            <button type="button" className="btn-save" onClick={handlePost} disabled={saving}>
              Proknjiži inventuru
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="mes-page">
      <Header title="MES - Skladište" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2500} />

          {active ? renderDetail(active) : renderList()}
        </div>
      </div>

      {showNewModal && (
        <NewCountModal
          warehouses={warehouses}
          lookups={lookups}
          onClose={() => setShowNewModal(false)}
          onCreated={(created) => {
            setShowNewModal(false);
            setActive(created);
            setDrafts({});
          }}
        />
      )}

      {showAddLineModal && active && (
        <AddLineModal
          countId={active.inventory_count_id}
          lookups={lookups}
          onClose={() => setShowAddLineModal(false)}
          onAdded={(updated) => {
            setShowAddLineModal(false);
            setActive(updated);
          }}
        />
      )}
    </div>
  );
};

// ==================== NOVA INVENTURA ====================

interface NewCountModalProps {
  warehouses: Warehouse[];
  lookups: InventoryCountLookups | null;
  onClose: () => void;
  onCreated: (count: InventoryCountDetail) => void;
}

const NewCountModal: React.FC<NewCountModalProps> = ({
  warehouses,
  lookups,
  onClose,
  onCreated,
}) => {
  const [warehouseId, setWarehouseId] = useState<number | "">("");
  const [countDate, setCountDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [typeCode, setTypeCode] = useState("monthly");
  const [includeCatalogue, setIncludeCatalogue] = useState(false);
  const [loading, setLoading] = useState(false);

  const isInitial = typeCode === "initial";

  useEffect(() => {
    if (warehouseId === "" && warehouses.length > 0) setWarehouseId(warehouses[0].warehouse_id);
  }, [warehouses, warehouseId]);

  const handleCreate = async () => {
    if (!warehouseId) {
      toast.error("Odaberite skladište");
      return;
    }
    try {
      setLoading(true);
      const created = await mesApi.createInventoryCount({
        warehouse_id: Number(warehouseId),
        count_date: countDate,
        counted_by: currentUserId(),
        notes: notes.trim() || undefined,
        type_code: typeCode,
        include_catalogue: isInitial || includeCatalogue,
      });
      toast.success(`Inventura ${created.count_number} otvorena`);
      onCreated(created);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mes-modal-overlay" onClick={onClose}>
      <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Nova inventura</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Skladište *</label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Odaberite skladište</option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label>Datum popisa *</label>
              <input
                type="date"
                value={countDate}
                onChange={(e) => setCountDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Vrsta popisa</label>
              <select value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
                {(lookups?.types ?? []).map((t) => (
                  <option key={t.id} value={t.code ?? ""}>
                    {TYPE_LABEL[t.code ?? ""] ?? t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="iwt-role-hint">
            {isInitial
              ? "Izlistava cijeli šifrarnik sirovina, ambalaže i repromaterijala s nultim stanjem — upišete samo količine koje su fizički u skladištu."
              : "Popisuju se LOT-ovi koji trenutno imaju stanje na skladištu."}
          </p>

          {!isInitial && (
            <div className="form-group">
              <label className="inv-checkbox">
                <input
                  type="checkbox"
                  checked={includeCatalogue}
                  onChange={(e) => setIncludeCatalogue(e.target.checked)}
                />
                <span>Dodaj i artikle bez stanja (za naknadni unos zatečene robe)</span>
              </label>
            </div>
          )}

          <div className="form-group">
            <label>Napomena</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Odustani
          </button>
          <button type="button" className="btn-save" onClick={handleCreate} disabled={loading}>
            {loading ? "Otvaranje..." : "Otvori popis"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== DODAVANJE STAVKE ====================

interface AddLineModalProps {
  countId: number;
  lookups: InventoryCountLookups | null;
  onClose: () => void;
  onAdded: (count: InventoryCountDetail) => void;
}

/** Sirovine i aliasi dijele jedan izbornik, pa se aliasi kodiraju negativnim id-em. */
const itemKey = (item: CountableItem) =>
  item.ingredient_id ?? -(item.ingredient_alias_id ?? 0);

const AddLineModal: React.FC<AddLineModalProps> = ({ countId, lookups, onClose, onAdded }) => {
  const [items, setItems] = useState<CountableItem[]>([]);
  const [selected, setSelected] = useState(0);
  const [lotNumber, setLotNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mesApi
      .getCountableItems()
      .then((data) => setItems(data || []))
      .catch((err) => toast.error(err.message));
  }, []);

  const options = useMemo(
    () =>
      items.map((item) => ({
        value: itemKey(item),
        label: item.material_name,
        sublabel: item.material_type ?? undefined,
      })),
    [items]
  );

  const handleAdd = async () => {
    const item = items.find((i) => itemKey(i) === selected);
    if (!item) {
      toast.error("Odaberite stavku");
      return;
    }
    const qty = quantity.trim() ? parseFloat(quantity.trim().replace(",", ".")) : null;
    if (qty != null && !Number.isFinite(qty)) {
      toast.error("Neispravna količina");
      return;
    }
    const price = unitPrice.trim() ? parseFloat(unitPrice.trim().replace(",", ".")) : undefined;

    try {
      setLoading(true);
      const updated = await mesApi.addInventoryCountLine(countId, {
        ingredient_id: item.ingredient_id ?? undefined,
        ingredient_alias_id: item.ingredient_alias_id ?? undefined,
        material_name: item.material_name,
        lot_number: lotNumber.trim() || undefined,
        expiry_date: expiryDate || undefined,
        counted_quantity_kg: qty,
        unit_price: Number.isFinite(price as number) ? price : undefined,
        reason_id: reasonId ? Number(reasonId) : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success("Stavka dodana");
      onAdded(updated);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mes-modal-overlay" onClick={onClose}>
      <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dodaj stavku u popis</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Stavka *</label>
            <SearchableSelect
              value={selected}
              options={options}
              onChange={(val) => setSelected(val)}
              placeholder="Pretraži sirovine, ambalažu i repro..."
            />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label>LOT / šarža</label>
              <input
                type="text"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                placeholder="Broj s ambalaže"
              />
            </div>
            <div className="form-group">
              <label>Rok trajanja</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Popisana količina (kg)</label>
              <input
                type="text"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Nabavna cijena (€/kg)</label>
              <input
                type="text"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
              />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Razlog odstupanja</label>
              <select
                value={reasonId}
                onChange={(e) => setReasonId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">—</option>
                {(lookups?.reasons ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Napomena</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <p className="iwt-role-hint">
            Knjiženjem inventure za ovu stavku se otvara novi LOT s popisanom količinom.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Odustani
          </button>
          <button type="button" className="btn-save" onClick={handleAdd} disabled={loading}>
            {loading ? "Dodavanje..." : "Dodaj"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Inventura;
